import { common } from '@kurtosys/ksys-app-template';

import { IBarScaleCalculatorOptions } from './models/IBarScaleCalculatorOptions.js';
import { IBarScaleProps } from './models/index.js';

type TZeroAxisPosition = 'left' | 'diff' | 'right';
interface IBarScaleRawDomain {
	min: number;
	absoluteMin: number;
	max: number;
	absoluteMax: number;
	size: number;
}

export class BarScaleCalculator<TData extends Record<string, any> = Record<string, any>> {
	private domain: IBarScaleRawDomain;
	private zeroAxisPosition: TZeroAxisPosition = 'diff';
	private applyNegativeModifier = false;
	private workingData: { calcValue: number; origValue: number }[];
	private lineX: number;

	protected getRowValue: (row: TData) => any;

	rawData: TData[];
	options: IBarScaleCalculatorOptions;

	constructor(rawData: TData[], getRowValue: (row: TData) => any, options: IBarScaleCalculatorOptions) {
		this.rawData = rawData;
		this.getRowValue = getRowValue;
		this.options = options;
		this.domain = this.getDomain();
		this.workingData = this.getWorkingData();
		this.lineX = this.getLineX();
	}

	public getPropsForDataIndex(
		index: number,
		optionOverrides: Partial<IBarScaleCalculatorOptions> = {},
	): IBarScaleProps | undefined {
		if (this.workingData.length > index && index >= 0) {
			const data = this.workingData[index];
			const width = this.getItemWidth(data.calcValue, false);
			const height = this.options.container.height - this.options.container.padding.vertical;
			const x = this.getItemX(width, this.lineX);
			return {
				container: common.commonUtils.deepMergeObjectsWithOptions(
					{ arrayMergeStrategy: 'DeepCopy' },
					this.options.container,
					optionOverrides.container,
				),
				line: {
					...common.commonUtils.deepMergeObjectsWithOptions(
						{ arrayMergeStrategy: 'DeepCopy' },
						this.options.line,
						optionOverrides.line,
					),
					x: this.lineX,
				},
				rect: {
					...common.commonUtils.deepMergeObjectsWithOptions(
						{ arrayMergeStrategy: 'DeepCopy' },
						this.options.rect,
						optionOverrides.rect,
					),
					width,
					height,
					x,
				},
				label: {
					...common.commonUtils.deepMergeObjectsWithOptions(
						{ arrayMergeStrategy: 'DeepCopy' },
						this.options.label,
						optionOverrides.label,
					),
					text: data.origValue.toString(),
				},
			};
		}
		return;
	}

	private getItemX(width: number, relativeX?: number) {
		switch (this.zeroAxisPosition) {
			case 'diff':
				if (relativeX) {
					return width < 0 ? relativeX + width : relativeX;
				}
				return width;
			case 'right':
				return this.options.container.width - width;
			case 'left':
			default:
				return 0;
		}
	}

	private getItemWidth(dataPoint: number, applyAbsolute = true) {
		if (dataPoint !== 0) {
			const percentage = dataPoint / this.domain.size;
			if (this.zeroAxisPosition === 'diff') {
				if (applyAbsolute) {
					return Math.abs(
						percentage * (this.options.container.width - this.options.container.padding.horizontal * 2),
					);
				}
				return percentage * (this.options.container.width - this.options.container.padding.horizontal * 2);
			}
			return percentage * (this.options.container.width - this.options.container.padding.horizontal);
		}
		return dataPoint;
	}

	private getLineX() {
		let lineX = 0;
		const minWidth = this.getItemWidth(this.domain.min);
		switch (this.zeroAxisPosition) {
			case 'diff':
				lineX = this.getItemX(minWidth) + this.options.container.padding.horizontal;
				break;
			case 'right':
				lineX = this.options.container.width;
				break;
			case 'left':
			default:
				lineX = 0;
		}
		return lineX;
	}

	private isNumber(value: any): value is number {
		return typeof value === 'number';
	}

	private getWorkingData() {
		return this.rawData.map((d) => {
			let value = this.getRowValue(d);
			if (this.isNumber(value) && value !== 0) {
				if (this.applyNegativeModifier) {
					if (this.zeroAxisPosition === 'right') {
						return { calcValue: Math.abs(value), origValue: value };
					}
				}
			} else if (!this.isNumber(value)) {
				value = 0;
			}
			return { calcValue: value, origValue: value };
		});
	}

	private getDomain() {
		const max = this.findDomainKey('max');
		const min = this.findDomainKey('min');
		const absoluteMax = Math.abs(max);
		const absoluteMin = Math.abs(min);

		let size = 0;
		if (min < 0 && max <= 0) {
			this.applyNegativeModifier = true;
			this.zeroAxisPosition = 'right';
			size = absoluteMin;
		} else if (min >= 0 && max > 0) {
			this.zeroAxisPosition = 'left';
			size = absoluteMax;
		} else if (min < 0 && max > 0) {
			this.applyNegativeModifier = true;
			this.zeroAxisPosition = 'diff';
			size = absoluteMin + absoluteMax;
		}

		return {
			max,
			min,
			absoluteMax,
			absoluteMin,
			size,
		};
	}

	private findDomainKey(key: 'min' | 'max') {
		return this.rawData.reduce((m: number, c: Record<string, any>) => {
			if (key === 'min') {
				return c.value < m ? c.value : m;
			}
			return c.value > m ? c.value : m;
		}, this.rawData[0].value);
	}
}
